import { useState } from 'react';
import { usePatronSelector } from '../common/use-patron-selector';
import { contentSelectors } from '../models/content';
import { programSelectors } from '../models/program';
import { advocateSelectors } from '../models/advocate';
import { trackContentCardPdfExport } from '../models/content/analytics';
import i18n from '../config/i18n';
import { localizeDate } from '../lib/date-formatter';
import {
  exportContentToPdf,
  generateQrSvg,
  buildPdfFilename,
  formatExportedAt,
} from '../lib/pdf-export';

// Format read time exactly as the on-screen content-detail info-header does
// (src/components/v2/content-detail/info-header.tsx) — same source field
// (read_time_in_seconds via getContentReadTime) and the same i18n string —
// so the PDF shows the identical "N minutes to read" the user already sees.
const formatReadTime = (readTimeSecs: number | null | undefined): string => {
  const secs = readTimeSecs || 60;
  const isHours = secs > 60 * 60;
  return i18n.t(`content.time_to_read.${isHours ? 'hours' : 'minutes'}`, {
    count: Math.max(Math.round(isHours ? secs / 60 / 60 : secs / 60), 1),
  });
};

// Client-side PDF export: loads the article embed into a hidden same-origin
// iframe, applies the print header/CSS, and opens the browser's print dialog
// so the user saves the PDF locally.
export const usePdfExport = (contentId: number) => {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const title = usePatronSelector((state) =>
    contentSelectors.getContentFunctionalTitle(state, { contentId })
  );
  const sourceName = usePatronSelector((state) =>
    contentSelectors.getContentSourceName(state, { contentId })
  );
  const sourceAvatar = usePatronSelector((state) =>
    contentSelectors.getContentSourceAvatar(state, { contentId })
  );
  const publishedAt = usePatronSelector((state) =>
    contentSelectors.getContentPublishedAt(state, { contentId })
  );
  const readTimeSecs = usePatronSelector((state) =>
    contentSelectors.getContentReadTime(state, { contentId })
  );
  const channels = usePatronSelector((state) =>
    contentSelectors.getContentChannels(state, { contentId })
  );
  const permalinkUrl = usePatronSelector((state) =>
    contentSelectors.getContentPermalinkUrl(state, { contentId })
  );
  const programId = usePatronSelector((state) =>
    programSelectors.getProgramId(state)
  );
  const advocateName = usePatronSelector((state) =>
    advocateSelectors.getAdvocateName(state)
  );

  const exportPdf = async () => {
    if (generating) return;
    setGenerating(true);
    setError(null);
    try {
      trackContentCardPdfExport(contentId, { location: 'content_detail' });

      const qrSvg = await generateQrSvg(
        permalinkUrl || `${window.location.origin}/contents/${contentId}`
      );
      const exportedAtStr = formatExportedAt();

      // Raw values — injectPdfHeader inserts them as text (DOM-escaped) and
      // sanitizes the avatar color/URL itself, so no pre-escaping here.
      await exportContentToPdf({
        embedUrl: `/embed/article/${contentId}?program=${programId}`,
        documentTitle: buildPdfFilename(contentId, title || '', exportedAtStr),
        headerData: {
          title: title || '',
          sourceName: sourceName || '',
          sourceAvatarUrl: sourceAvatar?.url || '',
          sourceAvatarColor: sourceAvatar?.color || '#888888',
          publishedAtStr: publishedAt ? localizeDate(publishedAt) : '',
          readTimeStr: formatReadTime(readTimeSecs || null),
          channels: (channels || []).map((c) => c.name).filter(Boolean),
          exportedBy: advocateName || 'Unknown',
          exportedAtStr,
          qrSvg,
        },
      });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'PDF export failed';
      setError(message);
    } finally {
      setGenerating(false);
    }
  };

  return { exportPdf, generating, error };
};
